// src/oura/visibility/useQuestionAnswers.ts
//
// What each engine actually said, for the question a reader opens (#3555).
//
// The index is fetched once and carries no bodies; a body is fetched only
// when a question is opened, and is then kept, so reopening costs nothing.
// Rows still open without it: the scan keeps a trimmed answer on every probe,
// and the drawer shows that while the full one loads, or instead of it.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getAssessmentAnswer,
    getAssessmentAnswersIndex,
    type AeoAnswerIndexEntry,
    type AeoAnswersIndex,
    type AeoQuestionAnswer,
} from "@service/aeo";
import type { EngineFilter, VisEngine } from "./model";

export type AnswerLoad =
    | { kind: "loading" }
    | { kind: "error"; message: string }
    /** Both requests came back 404: the question is not on this scan. */
    | { kind: "missing" }
    | {
        kind: "ready";
        /** The question's number, which is the screenshot's address. */
        n: number | null;
        byEngine: Partial<Record<VisEngine, AeoQuestionAnswer>>;
        /** The default list answered, so an engine absent from it had nothing stored. */
        complete: boolean;
    };

/** The answer store names instruments; the columns are engines. */
function engineOfAnswer(engine: string): VisEngine | null {
    const e = engine.toLowerCase();
    if (e === "chatgpt_app" || e === "openai" || e === "chatgpt") return "chatgpt";
    if (e === "gemini" || e === "google") return "gemini";
    // Google's AI was missing here, so its full overview was fetched by nobody
    // and dropped by this function if it ever had been (#3947).
    if (e === "google_ai" || e === "google_serp") return "ai_overview";
    return null;
}

/**
 * Google's answers are stored under their own instrument (#3947,
 * ourteam-backend#5123), so they are a second index, not a second engine in
 * the first one.
 */
const GOOGLE_INSTRUMENT = "google_serp";

const keyOf = (text: string) => text.trim().toLowerCase();

export interface QuestionAnswers {
    /** Null while loading, when there is no scan, or when the index failed. */
    index: AeoAnswersIndex | null;
    entryFor: (key: string) => AeoAnswerIndexEntry | undefined;
    stateFor: (key: string) => AnswerLoad | undefined;
    load: (question: string) => void;
    /**
     * Whether the engine a reader has filtered to has its answers stored.
     *
     * Per engine, because the two instruments disagree in BOTH directions and
     * one flag for the screen was wrong either way: on THE LUSH CLINIC's 24 Sep
     * scan ChatGPT's answers are stored and Google's are not, and the banner
     * stayed silent over a Google column that had nothing behind it; on a scan
     * whose Google was re-read but whose ChatGPT was never captured it says the
     * evidence does not exist while the route serves it in full.
     *
     * `true` while an index is still loading: a banner that flashes "not
     * stored" and then withdraws it is worse than one that arrives late.
     */
    storedFor: (engine: EngineFilter) => boolean;
    /**
     * Where the engine placed the business on one question, 1 = first, from
     * the index so the table draws it without opening a row. Null when it was
     * not named, was named outside any numbered list, or the index has not
     * arrived. The number is the answer's own, never inferred here.
     */
    rankFor: (key: string, engine: VisEngine) => number | null;
}

export function useQuestionAnswers(assessmentId: string | undefined, instrument: string | undefined): QuestionAnswers {
    const [index, setIndex] = useState<AeoAnswersIndex | null>(null);
    /** Google's own index, fetched beside the run's. */
    const [googleIndex, setGoogleIndex] = useState<AeoAnswersIndex | null>(null);
    /**
     * The index with no instrument: the one the backend lists by default. Gemini's
     * rows (and their `rank`) live here, not in an index filtered to the run's
     * instrument (#4097), so the table reads places from both.
     */
    const [defaultIndex, setDefaultIndex] = useState<AeoAnswersIndex | null>(null);
    const [states, setStates] = useState<Record<string, AnswerLoad>>({});
    /**
     * The scan the states belong to. States are keyed by question text, which
     * two scans share, so an answer still loading when `?scan=` changes would
     * otherwise land in the new scan's drawer, and that drawer would never
     * fetch its own.
     */
    const scanRef = useRef(assessmentId);

    useEffect(() => {
        scanRef.current = assessmentId;
        setIndex(null);
        setGoogleIndex(null);
        setDefaultIndex(null);
        setStates({});
        if (!assessmentId) return;
        let cancelled = false;
        // Ask for the instrument the run used, so the list matches the figure
        // it explains. A failed index is not an error state for the screen:
        // every row still opens, on the probe's own excerpt.
        const primary = getAssessmentAnswersIndex(assessmentId, instrument);
        primary
            .then((idx) => { if (!cancelled) setIndex(idx); })
            .catch(() => { if (!cancelled) setIndex(null); });
        // The unfiltered index, for the engines the run's instrument does not
        // list (Gemini on a ChatGPT-app run). With no instrument it IS the
        // primary request, so it is reused rather than asked twice.
        (instrument ? getAssessmentAnswersIndex(assessmentId) : primary)
            .then((idx) => { if (!cancelled) setDefaultIndex(idx); })
            .catch(() => { if (!cancelled) setDefaultIndex(null); });
        // Google's index is a second request, not a second engine in the first:
        // the store keys it by its own instrument. An older scan has none, and
        // the 404 lands as `stored: false`, which is the truth about it.
        //
        // Unless the run's own instrument IS Google's, in which case the two
        // requests are the same request (Jack, #3944) and the promise is reused.
        (instrument === GOOGLE_INSTRUMENT ? primary : getAssessmentAnswersIndex(assessmentId, GOOGLE_INSTRUMENT))
            .then((idx) => { if (!cancelled) setGoogleIndex(idx); })
            .catch(() => { if (!cancelled) setGoogleIndex(null); });
        return () => { cancelled = true; };
    }, [assessmentId, instrument]);

    const byQuestion = useMemo(() => {
        const map = new Map<string, AeoAnswerIndexEntry>();
        (index?.questions ?? []).forEach((q) => map.set(keyOf(q.question), q));
        return map;
    }, [index]);
    /** Google's own index, keyed the same way: its ranks live there, not in the run's. */
    const googleByQuestion = useMemo(() => {
        const map = new Map<string, AeoAnswerIndexEntry>();
        (googleIndex?.questions ?? []).forEach((q) => map.set(keyOf(q.question), q));
        return map;
    }, [googleIndex]);
    /** The unfiltered index, keyed the same way: where the non-run engines' rows are (#4097). */
    const defaultByQuestion = useMemo(() => {
        const map = new Map<string, AeoAnswerIndexEntry>();
        (defaultIndex?.questions ?? []).forEach((q) => map.set(keyOf(q.question), q));
        return map;
    }, [defaultIndex]);

    const load = useCallback((question: string) => {
        if (!assessmentId) return;
        const key = keyOf(question);
        const n = (byQuestion.get(key) ?? defaultByQuestion.get(key))?.n;
        const scan = assessmentId;
        /** Only while the scan asked about is still the one on screen. */
        const settle = (state: AnswerLoad) => {
            if (scanRef.current === scan) setStates((cur) => ({ ...cur, [key]: state }));
        };
        setStates((cur) => ({ ...cur, [key]: { kind: "loading" } }));
        /**
         * TWO requests, because one endpoint serves two things (#3616). The
         * app's own list is the only one carrying the screenshot; the default
         * list is where Gemini's answers live. Either may fail alone, and only
         * both failing is an error: one engine missing must not hide the other.
         */
        let failures = 0;
        const ask = (inst?: string) =>
            getAssessmentAnswer(assessmentId, n ?? question, inst).catch(() => {
                failures += 1;
                return null;
            });
        // THREE requests now. Google's overview is stored under its own
        // instrument, so it arrives in neither of the other two — which is why
        // the drawer showed the scan's 600-character excerpt while the route
        // held the whole 1,448 (#3947).
        // The same dedup as the index: when the run is a Google one, its own
        // request already carries Google's answer.
        const primaryAsk = ask(instrument);
        const googleAsk = instrument === GOOGLE_INSTRUMENT ? primaryAsk : ask(GOOGLE_INSTRUMENT);
        void Promise.all([primaryAsk, ask(), googleAsk]).then(([app, all, google]) => {
            const container = app ?? all ?? google;
            if (!container) {
                // Only a clean pair of 404s may say "not on this scan"; a
                // failed request must never produce that sentence.
                settle(failures > 0 ? { kind: "error", message: "Please try again." } : { kind: "missing" });
                return;
            }
            // The app's answer first, then only the engines it does not carry:
            // the API's own ChatGPT answer beside the app's under one heading
            // is the confusion this screen exists to remove.
            const byEngine: Partial<Record<VisEngine, AeoQuestionAnswer>> = {};
            [...(app?.answers ?? []), ...(all?.answers ?? []), ...(google?.answers ?? [])].forEach((a) => {
                const e = engineOfAnswer(a.engine ?? "");
                if (e && !byEngine[e]) byEngine[e] = a;
            });
            settle({ kind: "ready", n: container.n ?? n ?? null, byEngine, complete: all !== null });
        });
    }, [assessmentId, instrument, byQuestion, defaultByQuestion]);

    const entryFor = useCallback((key: string) => byQuestion.get(key), [byQuestion]);
    const stateFor = useCallback((key: string) => states[key], [states]);
    const storedFor = useCallback(
        // Under "All engines" the banner is about the assistants, which is what
        // the headline and "Missing you" are counted from; Google's column
        // renders from the scan's own readings either way.
        (engine: EngineFilter) => {
            const idx = engine === "ai_overview" ? googleIndex : index;
            return idx ? idx.stored : true;
        },
        [index, googleIndex],
    );

    const rankFor = useCallback(
        (key: string, engine: VisEngine) => {
            // Google's places live in its own index; the assistants' in the run's
            // index first, then the unfiltered one (#4097). Each engine is read
            // only from the rows that name it, so one engine never borrows another's place.
            const entries = engine === "ai_overview"
                ? [googleByQuestion.get(key)]
                : [byQuestion.get(key), defaultByQuestion.get(key)];
            // The index may list two instruments under one column (the app's
            // row and the API's). The first with a place wins; the index is
            // asked for the run's own instrument, so the app's row leads it.
            for (const entry of entries) {
                for (const e of entry?.engines ?? []) {
                    if (engineOfAnswer(e.engine ?? "") !== engine) continue;
                    // rank_available: false is "no numbered list", so no place is shown
                    // even if a number came back. Absent is unknown, so the number is used.
                    if (e.rank_available === false) continue;
                    if (typeof e.rank === "number" && Number.isInteger(e.rank) && e.rank > 0) return e.rank;
                }
            }
            return null;
        },
        [byQuestion, defaultByQuestion, googleByQuestion],
    );

    return { index, entryFor, stateFor, load, storedFor, rankFor };
}
